import type { Payout } from '../types/payout';

/**
 * Represents a conflict where the same crypto symbol appears with multiple
 * different IDs across transactions. This typically happens when:
 * - Payouts are imported from CSV with one ID (e.g., 'pearl')
 * - A swap is manually entered via CoinGecko search using a different ID
 */
export interface CryptoIdConflict {
  /** Upper-cased symbol, e.g. "PRL" */
  symbol: string;
  /** All distinct {id, name} pairs seen for this symbol, in order of first appearance */
  variants: { id: string; name: string }[];
  /** Total number of transactions using any variant of this symbol */
  payoutCount: number;
}

/** All crypto references in a single payout */
function* cryptosInPayout(
  p: Payout
): Generator<{ id: string; symbol: string; name: string }> {
  if (p.crypto) yield p.crypto;
  if (p.sentCrypto) yield p.sentCrypto;
  if (p.receivedCrypto) yield p.receivedCrypto;
}

/**
 * Scans all payouts and returns conflicts: symbols that appear under more
 * than one distinct `id`.
 *
 * Ignores `custom-` IDs — those are already handled by UnresolvedTokensBanner.
 */
export function detectCryptoIdConflicts(payouts: Payout[]): CryptoIdConflict[] {
  // symbol (upper) → Map<id → name>
  const symbolMap = new Map<string, Map<string, string>>();
  // symbol (upper) → set of payout IDs affected
  const payoutCountMap = new Map<string, Set<string>>();

  for (const p of payouts) {
    for (const crypto of cryptosInPayout(p)) {
      const sym = crypto.symbol.toUpperCase();

      if (!symbolMap.has(sym)) {
        symbolMap.set(sym, new Map());
        payoutCountMap.set(sym, new Set());
      }

      symbolMap.get(sym)!.set(crypto.id, crypto.name);
      payoutCountMap.get(sym)!.add(p.id);
    }
  }

  const conflicts: CryptoIdConflict[] = [];

  for (const [symbol, idMap] of symbolMap) {
    if (idMap.size < 2) continue; // No conflict — unique ID for this symbol

    const variants = Array.from(idMap.entries()).map(([id, name]) => ({ id, name }));
    const payoutCount = payoutCountMap.get(symbol)?.size ?? 0;
    conflicts.push({ symbol, variants, payoutCount });
  }

  // Sort by payout count descending so most-impacted conflicts appear first
  return conflicts.sort((a, b) => b.payoutCount - a.payoutCount);
}

/**
 * Replaces all occurrences of `oldId` with `newCrypto` in every payout.
 * Touches crypto, sentCrypto and receivedCrypto fields.
 */
export function normalizeConflict(
  payouts: Payout[],
  oldId: string,
  newCrypto: { id: string; symbol: string; name: string }
): Payout[] {
  function maybeReplace(
    crypto: Payout['crypto']
  ): Payout['crypto'] {
    if (!crypto || crypto.id !== oldId) return crypto;
    return { id: newCrypto.id, symbol: newCrypto.symbol, name: newCrypto.name };
  }

  return payouts.map((p) => ({
    ...p,
    crypto: maybeReplace(p.crypto),
    sentCrypto: maybeReplace(p.sentCrypto),
    receivedCrypto: maybeReplace(p.receivedCrypto),
  }));
}
